import cx from "clsx"
import { describedBy } from "./describedBy"
import { FieldHint } from "./FieldHint"
import styles from "./RadioGroup.module.css"

type Option = { value: string; label: string }

type Props = {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  options: ReadonlyArray<Option>
  disabled?: boolean
  error?: string
  /** How to answer it, shown under the label and read with the control. */
  hint?: string
  required?: boolean
  name?: string
}

// Components/Forms/Radio Button: every choice visible at once, one choosable.
// The browser's own radio is the control, transparent over the design's drawn
// mark, so it is what is clicked, focused and announced.
export function RadioGroup({
  id,
  label,
  value,
  onChange,
  options,
  disabled,
  error,
  hint,
  required,
  name,
}: Props) {
  return (
    <fieldset
      role="radiogroup"
      className={cx(styles.group, { [styles.error]: error })}
      aria-invalid={!!error}
      aria-required={required || undefined}
      aria-describedby={describedBy(
        hint && `${id}-hint`,
        error && `${id}-error`,
      )}
    >
      <legend className={styles.label}>
        {label}
        {required && (
          <span aria-hidden="true" className={styles.required}>
            {" "}
            *
          </span>
        )}
      </legend>
      {hint && <FieldHint id={`${id}-hint`}>{hint}</FieldHint>}
      <div className={styles.options}>
        {options.map((option) => (
          <label key={option.value} className={styles.option}>
            <span className={styles.control}>
              <input
                type="radio"
                className={styles.input}
                name={name ?? id}
                value={option.value}
                checked={value === option.value}
                onChange={() => onChange(option.value)}
                disabled={disabled}
                required={required}
              />
              <span className={styles.mark} aria-hidden="true" />
            </span>
            {option.label}
          </label>
        ))}
      </div>
      {error && (
        <span
          id={`${id}-error`}
          className={styles.errorMessage}
          aria-live="polite"
        >
          {error}
        </span>
      )}
    </fieldset>
  )
}
